import type { MatchScales } from "./types";
import { SCALE_AXES, VALUE_WEIGHTS } from "./weights";

export interface ValuesScoreBreakdown {
  loyalty: number;
  honesty: number;
  passion: number;
  commitment: number;
  /** Weighted total 0–100 */
  total: number;
}

/**
 * Individual values score: weighted average of 1–10 scales → 0–100.
 * Commitment 30%, Honesty 25%, Loyalty 25%, Passion 20%.
 */
export function individualValuesScore(scales: MatchScales): ValuesScoreBreakdown {
  let total = 0;
  const parts = {} as Record<keyof MatchScales, number>;

  for (const axis of SCALE_AXES) {
    const contrib = (scales[axis] / 10) * VALUE_WEIGHTS[axis] * 100;
    parts[axis] = round1(contrib);
    total += contrib;
  }

  return {
    loyalty: parts.loyalty,
    honesty: parts.honesty,
    passion: parts.passion,
    commitment: parts.commitment,
    total: round1(total),
  };
}

/**
 * Pair values alignment 0–100 (higher = closer ratings).
 * Weighted inverse distance on each 1–10 axis.
 */
export function valuesScore(a: MatchScales, b: MatchScales): number {
  let score = 0;
  for (const axis of SCALE_AXES) {
    const distance = Math.abs(a[axis] - b[axis]); // 0–9
    const similarity = 1 - distance / 9;
    score += similarity * VALUE_WEIGHTS[axis] * 100;
  }
  return Math.round(Math.max(0, Math.min(100, score)));
}

export function scoreLabel(score: number): string {
  if (score >= 85) return "Strong alignment";
  if (score >= 70) return "Solid alignment";
  if (score >= 55) return "Moderate alignment";
  return "Different rhythms";
}

export function scaleDiffs(a: MatchScales, b: MatchScales) {
  return SCALE_AXES.map((k) => ({
    key: k,
    label: k.charAt(0).toUpperCase() + k.slice(1),
    weight: VALUE_WEIGHTS[k],
    self: a[k],
    other: b[k],
    delta: Math.abs(a[k] - b[k]),
  }));
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
